import Sentry from "@similarweb/sentry-logger";

import { ONBOARDING_AGENT_LOG_PREFIX } from "../../onboardingAgentConstants";
import type { ChatMessage } from "../OnboardingAgentPanel.types";

const ONBOARDING_AGENT_SESSION_STORAGE_KEY = "onboardingAgentConversationV2";
const ONBOARDING_AGENT_DRAFT_SESSION_STORAGE_KEY = "onboardingAgentDraftV1";

type SessionStorageOperation = "read" | "write" | "remove";

const logSessionStorageError = (operation: SessionStorageOperation, key: string, error: unknown): void => {
    Sentry.error(
        `${ONBOARDING_AGENT_LOG_PREFIX} Failed to ${operation} value in sessionStorage (key: "${key}"):`,
        error as Error,
    );
};

type OnboardingAgentStoredConversation = {
    readonly messages: ChatMessage[];
    readonly threadId: string | null;
};

const EMPTY_CONVERSATION: OnboardingAgentStoredConversation = { messages: [], threadId: null };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isChatMessage = (value: unknown): value is ChatMessage => {
    if (!isRecord(value) || typeof value.id !== "string" || typeof value.text !== "string") {
        return false;
    }

    if (value.role === "user") {
        return true;
    }

    return value.role === "assistant" && typeof value.copyPlainText === "string";
};

export const loadOnboardingAgentConversation = (): OnboardingAgentStoredConversation => {
    try {
        const storedValue = window.sessionStorage.getItem(ONBOARDING_AGENT_SESSION_STORAGE_KEY);
        if (!storedValue) {
            return EMPTY_CONVERSATION;
        }

        const parsed: unknown = JSON.parse(storedValue);
        if (!isRecord(parsed)) {
            return EMPTY_CONVERSATION;
        }

        const messages = Array.isArray(parsed.messages) ? parsed.messages.filter(isChatMessage) : [];
        const threadId = typeof parsed.threadId === "string" ? parsed.threadId : null;

        return { messages, threadId };
    } catch (error) {
        logSessionStorageError("read", ONBOARDING_AGENT_SESSION_STORAGE_KEY, error);

        return EMPTY_CONVERSATION;
    }
};

export const saveOnboardingAgentConversation = (conversation: OnboardingAgentStoredConversation): void => {
    try {
        window.sessionStorage.setItem(ONBOARDING_AGENT_SESSION_STORAGE_KEY, JSON.stringify(conversation));
    } catch (error) {
        logSessionStorageError("write", ONBOARDING_AGENT_SESSION_STORAGE_KEY, error);
    }
};

export const loadOnboardingAgentDraft = (): string => {
    try {
        return window.sessionStorage.getItem(ONBOARDING_AGENT_DRAFT_SESSION_STORAGE_KEY) ?? "";
    } catch (error) {
        logSessionStorageError("read", ONBOARDING_AGENT_DRAFT_SESSION_STORAGE_KEY, error);

        return "";
    }
};

export const saveOnboardingAgentDraft = (draft: string): void => {
    try {
        if (draft.length === 0) {
            window.sessionStorage.removeItem(ONBOARDING_AGENT_DRAFT_SESSION_STORAGE_KEY);
            return;
        }
        window.sessionStorage.setItem(ONBOARDING_AGENT_DRAFT_SESSION_STORAGE_KEY, draft);
    } catch (error) {
        const operation: SessionStorageOperation = draft.length === 0 ? "remove" : "write";
        logSessionStorageError(operation, ONBOARDING_AGENT_DRAFT_SESSION_STORAGE_KEY, error);
    }
};
